import React, { useState } from 'react';
import SingleChoice from './SingleChoice';
import MultiChoice from './MultiChoice';
import DragDrop from './DragDrop';
import DomainBadge from '../../../shared/components/DomainBadge';
import { renderMarkdown } from '../../../shared/utils/markdownLite';

/**
 * Scenario/Testlet question: pinned scenario context with 2-4 sub-questions.
 * The scenario stays visible while the user answers each sub-question.
 */
export default function ScenarioTestlet({ question, answer = {}, onChange }) {
  const [currentSubIdx, setCurrentSubIdx] = useState(0);
  const subQuestions = question.sub_questions || [];

  if (subQuestions.length === 0) return null;

  const currentSub = subQuestions[currentSubIdx];

  const handleSubAnswer = (subAnswer) => {
    const newAnswers = { ...answer, [currentSub.id]: subAnswer };
    onChange(newAnswers);
  };

  return (
    <div>
      {/* Scenario context — always visible */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-4 sticky top-16 z-10">
        <div className="flex items-center gap-2 mb-2">
          <DomainBadge domainId={question.domain} size="xs" />
          <span className="text-xs text-blue-600 font-medium">Scenario</span>
        </div>

        {/* Network diagram */}
        {question.diagram && (
          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg mb-3 text-xs font-mono overflow-x-auto">
            {question.diagram}
          </pre>
        )}

        <div className="text-gray-800 text-sm leading-relaxed">
          {renderMarkdown(question.scenario_context || question.prompt)}
        </div>
      </div>

      {/* Sub-question tabs */}
      <div className="flex gap-2 mb-4">
        {subQuestions.map((sq, idx) => (
          <button
            key={sq.id}
            onClick={() => setCurrentSubIdx(idx)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              idx === currentSubIdx
                ? 'bg-blue-600 text-white'
                : answer[sq.id] !== undefined
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Q{idx + 1}
            {answer[sq.id] !== undefined && idx !== currentSubIdx && ' ✓'}
          </button>
        ))}
      </div>

      {/* Current sub-question */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-sm text-gray-500 mb-2">
          Question {currentSubIdx + 1} of {subQuestions.length}
        </p>
        <div className="text-gray-900 mb-4">{renderMarkdown(currentSub.prompt)}</div>

        {currentSub.type === 'single_choice' && (
          <SingleChoice
            question={currentSub}
            answer={answer[currentSub.id]}
            onChange={handleSubAnswer}
          />
        )}
        {currentSub.type === 'multi_choice' && (
          <MultiChoice
            question={currentSub}
            answer={answer[currentSub.id] || []}
            onChange={handleSubAnswer}
          />
        )}
        {currentSub.type === 'drag_drop' && (
          <DragDrop
            question={currentSub}
            answer={answer[currentSub.id] || {}}
            onChange={handleSubAnswer}
          />
        )}
      </div>

      {/* Sub-question navigation */}
      <div className="flex justify-between mt-4">
        <button
          onClick={() => setCurrentSubIdx(Math.max(0, currentSubIdx - 1))}
          disabled={currentSubIdx === 0}
          className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30"
        >
          ← Previous sub-question
        </button>
        <button
          onClick={() => setCurrentSubIdx(Math.min(subQuestions.length - 1, currentSubIdx + 1))}
          disabled={currentSubIdx >= subQuestions.length - 1}
          className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-30"
        >
          Next sub-question →
        </button>
      </div>
    </div>
  );
}
